/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDFInvoiceHeader } from './PDFInvoiceHeader';
import PDFTable from 'src/pages/cart/components/pdf/PDFTable';
import moment from 'moment';

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
    lineHeight: 1.5,
    color: '#3E4B5B',
    flexDirection: 'column',
    fontFamily: 'Khmer',
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    marginBottom: 10,
    fontFamily: 'Khmer',
    fontSize: '10pt',
  },
});

interface MonthlyInvoicesDocumentProps {
  invoices: any[];
  type?: 'fbl-delivery' | 'general-delivery' | 'inventory-sale' | 'platform-fee';
}

export function MonthlyInvoicesDocument({ invoices, type }: MonthlyInvoicesDocumentProps) {
  if (!invoices || invoices.length === 0) return null;

  return (
    <Document>
      {invoices.map((invoice: any, idx: number) => {
        const total_usd = Number(invoice.total_usd ?? invoice.amount) || 0;
        const vat_included = invoice.vat_included !== undefined ? Number(invoice.vat_included) : total_usd * (10 / 110);

        const items =
          invoice.items && invoice.items.length > 0
            ? invoice.items
            : [
                {
                  description:
                    invoice.description ||
                    (type === 'inventory-sale'
                      ? 'ទំនិញ'
                      : type === 'platform-fee'
                      ? 'សេវារៀបចំវេចខ្ចប់និងបញ្ញើទំនិញ'
                      : 'សេវាដឹកជញ្ជូន'),
                  qty: invoice.qty || 1,
                  unit_price:
                    invoice.unit_price !== undefined ? Number(invoice.unit_price) : total_usd / (invoice.qty || 1),
                  amount: total_usd,
                },
              ];

        const isSupplier = type === 'platform-fee' || (invoice.supplier_name && !invoice.customer_name);
        const isDelivery = type === 'fbl-delivery' || type === 'general-delivery';

        const invoiceNoText =
          (type === 'inventory-sale' || type === 'platform-fee') && !invoice.invoice_no?.startsWith('Invoice:')
            ? `Invoice: ${invoice.invoice_no}`
            : invoice.invoice_no;

        return (
          <Page key={`${invoice.invoice_no || 'inv'}-${idx}`} size="A4" style={styles.page}>
            <PDFInvoiceHeader />

            {/* Info */}
            <View style={styles.infoContainer}>
              <View>
                <Text style={{ fontWeight: 'bold', fontSize: '12pt', marginBottom: 5, fontFamily: 'Khmer' }}>
                  អតិថិជន
                </Text>

                {isSupplier ? (
                  <View>
                    <Text style={{ fontFamily: 'Khmer' }}>{invoice.supplier_name || 'Supplier'}</Text>
                    {invoice.supplier_address ? (
                      <Text style={{ fontFamily: 'Khmer' }}>{invoice.supplier_address}</Text>
                    ) : null}
                    {invoice.supplier_vat ? (
                      <Text style={{ fontFamily: 'Khmer' }}>VATIN: {invoice.supplier_vat}</Text>
                    ) : null}
                  </View>
                ) : isDelivery ? (
                  <Text style={{ fontFamily: 'Khmer' }}>+855 {invoice.customer_phone || ''}</Text>
                ) : (
                  <View>
                    <Text style={{ fontFamily: 'Khmer' }}>{invoice.customer_name || 'Customer'}</Text>
                    <Text style={{ fontFamily: 'Khmer' }}>+855 {invoice.customer_phone || ''}</Text>
                  </View>
                )}
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text>{moment.utc(invoice.invoice_date).format('DD/MM/YYYY')}</Text>
                <Text>{invoiceNoText}</Text>
              </View>
            </View>

            {/* Table */}
            <View style={{ marginTop: 10 }}>
              <PDFTable border>
                <PDFTable.thead>
                  <PDFTable.row>
                    <PDFTable.th text="Description" align="left" width={360} />
                    <PDFTable.th text="Qty." width={40} align="right" />
                    <PDFTable.th text="Unit Price" width={75} align="right" />
                    <PDFTable.th text="Amount" width={75} align="right" />
                  </PDFTable.row>
                </PDFTable.thead>
                <PDFTable.tbody>
                  {items.map((item: any, itemIdx: number) => (
                    <PDFTable.row key={itemIdx}>
                      <PDFTable.td text={item.description || ''} align="left" width={360} />
                      <PDFTable.td text={item.qty?.toString() || '1'} width={40} align="right" />
                      <PDFTable.td
                        text={`$${Number(item.unit_price || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                        })}`}
                        width={75}
                        align="right"
                      />
                      <PDFTable.td
                        text={`$${Number(item.amount || 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                        })}`}
                        width={75}
                        align="right"
                      />
                    </PDFTable.row>
                  ))}

                  {/* Totals */}
                  <PDFTable.row>
                    <PDFTable.td text="Total (USD)" align="left" width={360} />
                    <PDFTable.td text="" width={40} />
                    <PDFTable.td text="" width={75} />
                    <PDFTable.td
                      text={`$${total_usd.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                      width={75}
                      align="right"
                    />
                  </PDFTable.row>
                  <PDFTable.row>
                    <PDFTable.td text="VAT Included (10%)" align="left" width={360} />
                    <PDFTable.td text="" width={40} />
                    <PDFTable.td text="" width={75} />
                    <PDFTable.td
                      text={`$${vat_included.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                      width={75}
                      align="right"
                    />
                  </PDFTable.row>
                </PDFTable.tbody>
              </PDFTable>
            </View>

            {type === 'inventory-sale' || type === 'platform-fee' || invoice.shop_id !== undefined ? (
              <View style={{ marginTop: 20, paddingLeft: 5 }}>
                <Text style={{ fontSize: '10pt', fontFamily: 'Khmer' }}>Shop ID: {invoice.shop_id ?? ''}</Text>
              </View>
            ) : null}
          </Page>
        );
      })}
    </Document>
  );
}
